// Os passos e as trilhas do tour, iguais aos de tour.js:5-79.
//
// O seletor aponta para o elemento real do painel, e é o contrato entre o tour e
// as telas: mudar a classe de um cartão sem ajustar aqui faz o passo perder o
// alvo e cair no balão centralizado, sem recorte.
//
// O texto vem em markdown (o painel antigo guardava HTML solto) e é renderizado
// pelo mesmo `md()` do acervo — assim `código` e **ênfase** continuam saindo com
// o estilo que tinham, sem HTML interpolado à mão.

export interface Passo {
  sel: string;
  aba: string | null;
  title: string;
  text: string;
  doit: string;
}

export const PASSOS: Record<string, Passo> = {
  overview: {
    sel: '#pane-overview .agent-card',
    aba: 'overview',
    title: 'O acervo entra no seu Cursor',
    text: 'Este cartão instala o CLI e liga o MCP `context-engine` no seu clone. A partir daí o agente lê overview, rotas do AST, decisões e notas antes de reler o repositório.',
    doit: 'Copie o `mcp.json` ou rode `eng init-agent` no clone.',
  },
  notes: {
    sel: '#pane-overview .notes-strip',
    aba: 'overview',
    title: 'Nota: o que o código não mostra',
    text: 'Passe o mouse em qualquer trecho da doc e clique em **+**. A nota vira parte do pacote que o agente lê, acima da prosa gerada.',
    doit: 'Escreva uma nota (produção, negócio, gotcha) em um trecho.',
  },
  doc: {
    sel: '.sidebar #file-list',
    aba: 'overview',
    title: 'Doc por arquivo, com procedência',
    text: 'Cada arquivo tem versão, commit e o bloco **Fatos (AST)** — extraído do código, não do modelo. O cabeçalho mostra dono, frescor e decisões que o governam.',
    doit: 'Abra um arquivo da lista.',
  },
  graph: {
    sel: '#pane-graph .g-bar',
    aba: 'graph',
    title: 'O mapa do código, navegável',
    text: 'Camadas (rota → controller → serviço → arquivo) ou força. Clique num nó para ver assinatura, decorators, quem depende dele e abrir a doc.',
    doit: 'Busque um nó e clique nele.',
  },
  sessions: {
    sel: '#pane-sessions',
    aba: 'sessions',
    title: 'O que já foi tentado aqui',
    text: 'Cada tarefa vira uma sessão: o diário bruto (tentativa, beco, descoberta) fica guardado, e ao fechar o autor publica **um resumo**. Ele nasce **não confirmado** — já é buscável, e o dono do módulo confirma, edita ou contesta.',
    doit: 'Abra uma sessão e leia o diário — ou confirme um resumo que seja do seu módulo.',
  },
  memoria: {
    sel: '#pane-graph #g-mode',
    aba: 'graph',
    title: 'O mapa do que o time sabe',
    text: '**memória** desenha decisões, notas, sessões, lacunas e donos ligados aos arquivos. **cobertura** apaga o que ninguém documentou: clicar num arquivo apagado abre a nota ou a lacuna ali mesmo.',
    doit: 'Troque para **cobertura** e clique num arquivo apagado.',
  },
  impact: {
    sel: '#pane-impact .imp-in',
    aba: 'impact',
    title: 'O que quebra se eu mexer aqui',
    text: 'Dependentes vêm do grafo do AST, as rotas são os endpoints alcançados, e ainda vêm o dono e as decisões a respeitar. O agente recebe o mesmo com `impact_of`.',
    doit: 'Analise o impacto de um arquivo.',
  },
  review: {
    sel: '#pane-review',
    aba: 'review',
    title: 'A IA propõe, o time aprova',
    text: 'Quando a doc contradiz o código, o agente chama `propose_doc_update` e cai aqui. Você vê o diff, pode editar antes de aprovar, ou rejeitar com motivo — e o motivo volta para o agente.',
    doit: 'Abra uma proposta e leia o diff (ou peça uma ao agente no Cursor).',
  },
  decisions: {
    sel: '#pane-decisions',
    aba: 'decisions',
    title: 'Decisão registrada é decisão que não se repete',
    text: 'ADRs, runbooks e padrões ficam versionados como unidades do acervo, ligados aos arquivos que governam. Se o código citado desaparece, a decisão é marcada com **deriva**.',
    doit: 'Registre uma decisão (ou abra uma existente).',
  },
  health: {
    sel: '#pane-health .hcards',
    aba: 'health',
    title: 'Saúde: o que faz o TL confiar',
    text: 'Cobertura, doc atrás do HEAD, arquivos sem dono, lacunas abertas e buscas sem resposta. Declare donos por glob para toda pergunta ter endereço.',
    doit: 'Declare um dono ou importe o CODEOWNERS.',
  },
  palette: {
    sel: '#palette-btn',
    aba: null,
    title: 'Ctrl+K para chegar em qualquer coisa',
    text: 'Arquivo, rota, decisão, nota, proposta e as abas — tudo num campo.',
    doit: 'Aperte Ctrl+K e salte para um arquivo.',
  },
};

export type Trilha = 'junior' | 'pleno' | 'senior' | 'tech_lead';

export const TRILHAS: Record<Trilha, { label: string; hint: string; steps: string[] }> = {
  junior: {
    label: 'Júnior',
    hint: 'por onde começar',
    steps: ['overview', 'doc', 'sessions', 'notes', 'graph', 'palette'],
  },
  pleno: {
    label: 'Pleno',
    hint: 'não quebrar nada',
    steps: ['overview', 'doc', 'impact', 'graph', 'sessions', 'review', 'palette'],
  },
  senior: {
    label: 'Sênior',
    hint: 'registrar o que sabe',
    steps: ['notes', 'sessions', 'review', 'decisions', 'impact', 'palette'],
  },
  tech_lead: {
    label: 'Tech Lead / Arquiteto',
    hint: 'revisão e saúde',
    steps: ['review', 'sessions', 'decisions', 'memoria', 'health', 'impact', 'palette'],
  },
};
